<script setup lang="ts">
import type { TableColumn } from "@nuxt/ui"
import { computed, ref } from "vue"
import ChartPanel from "../components/ChartPanel.vue"
import ClippedValue from "../components/ClippedValue.vue"
import DashboardCard from "../components/DashboardCard.vue"
import DataState from "../components/DataState.vue"
import MetricTile from "../components/MetricTile.vue"
import NullBadge from "../components/NullBadge.vue"
import PageHeader from "../components/PageHeader.vue"
import SessionId from "../components/SessionId.vue"
import SortableTable from "../components/SortableTable.vue"
import StatusBadge from "../components/StatusBadge.vue"
import DashboardLayout from "../layouts/DashboardLayout.vue"
import { useDashboardStore } from "../stores/dashboard"
import type { RelayPair } from "../utils/dashboard-types"
import { formatBytes, formatNumber } from "../utils/format"

type TableSorting = Array<{ id: string; desc: boolean }>

const dashboard = useDashboardStore()
const relayPairSorting = ref<TableSorting>([{ id: "latencyMs", desc: true }])
const pairedCount = computed(() => dashboard.relayPairs.filter((pair) => pair.status === "paired").length)
const totalDatagrams = computed(() => {
  const emittedRates = dashboard.relayEndpoints
    .map((endpoint) => endpoint.datagramsPerSecond)
    .filter((value): value is number => value !== null)
  return emittedRates.length === 0 ? null : emittedRates.reduce((total, value) => total + value, 0)
})
const relayStatus = computed(() => {
  if (dashboard.relayEndpoints.some((endpoint) => endpoint.status === "offline")) {
    return "offline"
  }
  if (dashboard.relayEndpoints.some((endpoint) => endpoint.status === "degraded")) {
    return "degraded"
  }
  return dashboard.relayEndpoints.length > 0 ? "configured" : "not configured"
})
const relayPairColumns: TableColumn<RelayPair>[] = [
  {
    accessorKey: "sessionId",
    header: "Session",
    meta: {
      class: {
        td: "min-w-[140px]"
      }
    }
  },
  {
    accessorKey: "relayRegion",
    header: "Region"
  },
  {
    accessorKey: "status",
    header: "Status"
  },
  {
    accessorKey: "androidState",
    header: "Android"
  },
  {
    accessorKey: "browserState",
    header: "Browser"
  },
  {
    accessorKey: "latencyMs",
    header: "RTT"
  },
  {
    accessorKey: "lossPercent",
    header: "Loss"
  },
  {
    accessorKey: "receivedBytes",
    header: "Received"
  },
  {
    accessorKey: "sentBytes",
    header: "Sent"
  },
  {
    accessorKey: "idleSeconds",
    header: "Idle"
  },
  {
    accessorKey: "closeReason",
    header: "Close reason"
  }
]
</script>

<template>
  <DashboardLayout>
    <div class="dashboard-data-page flex min-w-0 flex-col gap-5">
      <PageHeader
        title="Relay and dataplane"
        description="Relay endpoints and pair telemetry."
        details="Shows configured relay endpoints, browser and Android participant state, dataplane counters, and telemetry fields that still need source coverage."
      >
        <template #actions>
          <StatusBadge :status="relayStatus" />
          <span class="dashboard-mono text-sm text-(--dash-muted)"
            >{{ formatNumber(dashboard.relayEndpoints.length) }} endpoints</span
          >
        </template>
      </PageHeader>

      <DataState
        :loading="dashboard.isLoading"
        :error="dashboard.errorMessage"
        :empty="false"
        empty-title="No relay records"
        empty-description="No relay dataplane records were returned by the Worker dashboard API."
      >
        <section class="dashboard-data-content flex min-w-0 flex-1 flex-col gap-4">
          <section class="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <MetricTile
              label="Relay endpoints"
              :value="formatNumber(dashboard.relayEndpoints.length)"
              tone="neutral"
              caption="Configured relay transports"
            />
            <MetricTile
              label="Relay events"
              :value="formatNumber(dashboard.relaySignals.eventCount)"
              tone="neutral"
              caption="Relay-related events retained"
            />
            <MetricTile
              label="Missing signals"
              :value="formatNumber(dashboard.relaySignals.missing.length)"
              tone="neutral"
              caption="Telemetry fields missing"
            />
            <MetricTile
              label="Datagrams/s"
              :value="totalDatagrams === null ? 'n/a' : formatNumber(totalDatagrams)"
              tone="neutral"
              caption="Endpoint datagram rate"
            />
          </section>

          <section class="grid gap-4 xl:grid-cols-[minmax(0,1fr)_420px]">
            <ChartPanel title="Relay participants" eyebrow="Emitted pairing state">
              <div class="grid min-h-[300px] gap-5 md:grid-cols-3">
                <div class="grid content-center gap-3 border border-(--dash-line) p-4 text-center">
                  <p class="dashboard-label">Browser client</p>
                  <p class="dashboard-mono dashboard-value text-3xl font-medium">{{ formatNumber(pairedCount) }}</p>
                  <p class="text-sm text-(--dash-muted)">paired rows</p>
                </div>
                <div
                  class="grid content-center gap-3 border border-(--dash-line) bg-[rgba(241,243,242,0.035)] p-4 text-center"
                >
                  <p class="dashboard-label">MASQUE relay</p>
                  <div class="mx-auto grid size-28 place-items-center border border-(--dash-line-strong)">
                    <span class="dashboard-mono text-2xl font-medium">{{ dashboard.relayEndpoints.length }}</span>
                  </div>
                  <p class="text-sm text-(--dash-muted)">configured transport endpoints</p>
                </div>
                <div class="grid content-center gap-3 border border-(--dash-line) p-4 text-center">
                  <p class="dashboard-label">Android target</p>
                  <p class="dashboard-mono dashboard-value text-3xl font-medium">{{ formatNumber(pairedCount) }}</p>
                  <p class="text-sm text-(--dash-muted)">paired rows</p>
                </div>
              </div>
            </ChartPanel>

            <DashboardCard title="Relay endpoints" eyebrow="Region, cloud, ports, and limits" static>
              <div class="grid gap-3">
                <article
                  v-for="endpoint in dashboard.relayEndpoints"
                  :key="endpoint.id"
                  class="border-b border-(--dash-line) pb-3 last:border-b-0 last:pb-0"
                >
                  <div class="flex items-center justify-between gap-3">
                    <span class="dashboard-mono text-sm">{{ endpoint.id }}</span>
                    <StatusBadge :status="endpoint.status" />
                  </div>
                  <div class="mt-3 grid grid-cols-2 gap-2 text-xs text-(--dash-muted)">
                    <span class="inline-flex items-center gap-1">
                      region=<span v-if="endpoint.region !== 'not-emitted'">{{ endpoint.region }}</span>
                      <NullBadge v-else field="region" />
                    </span>
                    <span class="inline-flex items-center gap-1">
                      cloud=<span v-if="endpoint.cloud">{{ endpoint.cloud }}</span>
                      <NullBadge v-else field="cloud" />
                    </span>
                    <span class="col-span-2 inline-flex min-w-0 items-center gap-1">
                      host=<span v-if="endpoint.host" class="truncate">{{ endpoint.host }}</span>
                      <NullBadge v-else field="host" />
                    </span>
                    <span class="inline-flex items-center gap-1">
                      listen=<span v-if="endpoint.listenPort !== null">{{ endpoint.listenPort }}</span>
                      <NullBadge v-else field="listen port" />
                    </span>
                    <span class="inline-flex items-center gap-1">
                      health=<span v-if="endpoint.healthPort !== null">{{ endpoint.healthPort }}</span>
                      <NullBadge v-else field="health port" />
                    </span>
                    <span class="inline-flex items-center gap-1">
                      ttl=<span v-if="endpoint.sessionTtlSeconds !== null">{{ endpoint.sessionTtlSeconds }}s</span>
                      <NullBadge v-else field="TTL" />
                    </span>
                    <span class="col-span-2 inline-flex items-center gap-1">
                      max=<span v-if="endpoint.maxDatagramBytes !== null">{{ endpoint.maxDatagramBytes }} bytes</span>
                      <NullBadge v-else field="max datagram bytes" />
                    </span>
                  </div>
                </article>
                <p v-if="dashboard.relayEndpoints.length === 0" class="text-sm leading-5 text-(--dash-muted)">
                  No relay transport endpoint is configured for this Worker environment.
                </p>
              </div>
            </DashboardCard>
          </section>

          <SortableTable
            v-model:sorting="relayPairSorting"
            :columns="relayPairColumns"
            :data="dashboard.relayPairs"
            title="Paired relay sessions"
            eyebrow="Dataplane counters"
            empty-title="No relay pair rows"
            empty-description="Relay pair telemetry has not been emitted yet."
            modal-description="Relay participant state, dataplane counters, latency, loss, idle time, and close reasons."
            :ui="{ root: 'dashboard-table min-w-[1180px]', th: 'whitespace-nowrap', td: 'whitespace-nowrap' }"
            sticky="header"
          >
            <template #sessionId-cell="{ row }">
              <SessionId :value="row.original.sessionId" max-class="max-w-[8rem]" />
            </template>
            <template #relayRegion-cell="{ row }">
              <span v-if="row.original.relayRegion !== 'not-emitted'" class="dashboard-mono">
                {{ row.original.relayRegion }}
              </span>
              <NullBadge v-else field="region" />
            </template>
            <template #status-cell="{ row }">
              <StatusBadge :status="row.original.status" />
            </template>
            <template #androidState-cell="{ row }">
              <StatusBadge :status="row.original.androidState" />
            </template>
            <template #browserState-cell="{ row }">
              <StatusBadge :status="row.original.browserState" />
            </template>
            <template #latencyMs-cell="{ row }">
              <span v-if="row.original.latencyMs !== null" class="dashboard-mono">
                {{ row.original.latencyMs }} ms
              </span>
              <NullBadge v-else field="RTT" />
            </template>
            <template #lossPercent-cell="{ row }">
              <span v-if="row.original.lossPercent !== null" class="dashboard-mono">
                {{ row.original.lossPercent }}%
              </span>
              <NullBadge v-else field="loss" />
            </template>
            <template #receivedBytes-cell="{ row }">
              <span v-if="row.original.receivedBytes !== null" class="dashboard-mono">
                {{ formatBytes(row.original.receivedBytes) }}
              </span>
              <NullBadge v-else field="received bytes" />
            </template>
            <template #sentBytes-cell="{ row }">
              <span v-if="row.original.sentBytes !== null" class="dashboard-mono">
                {{ formatBytes(row.original.sentBytes) }}
              </span>
              <NullBadge v-else field="sent bytes" />
            </template>
            <template #idleSeconds-cell="{ row }">
              <span class="dashboard-mono">{{ row.original.idleSeconds }} s</span>
            </template>
            <template #closeReason-cell="{ row }">
              <ClippedValue
                v-if="row.original.closeReason !== 'not emitted'"
                :value="row.original.closeReason"
                max-class="max-w-[20rem]"
                title="Close reason"
              />
              <NullBadge v-else field="close reason" />
            </template>
          </SortableTable>
        </section>
      </DataState>
    </div>
  </DashboardLayout>
</template>
